import { BeatResolution } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useLayoutEffect, useRef } from 'react'
import { Arp2Utils } from '../../../../client/SongUtils'
import { styled } from '../../../../lib/stitches'
import PianoRollStateManager, { IPianoRollState } from './PianoRollStateManager'
import { BACKGROUND_HEIGHT, KEYBOARD_WIDTH, useCurrentLoop, useSnapManager } from './PianoRollUtils'
import { SCROLLBAR_SIZE } from './VerticalScrollBox'

const GridCanvas = styled('canvas', {
  position: 'absolute',
  left: KEYBOARD_WIDTH,
  right: SCROLLBAR_SIZE,
  top: 0,
  opacity: 0.1,
  height: BACKGROUND_HEIGHT,
  overflow: 'hidden'
})

const DEFAULT_RESOLUTION = devicePixelRatio || 2

const render = (canvas: HTMLCanvasElement, resolution: BeatResolution, state: IPianoRollState) => {
  const { width } = state
  if (width === 0) return
  canvas.width = width * DEFAULT_RESOLUTION
  canvas.height = BACKGROUND_HEIGHT * DEFAULT_RESOLUTION
  const lengthOfRes = Arp2Utils.getLengthOfRes(resolution)
  const context = canvas.getContext('2d')
  context.restore()
  context.save()
  for (let i = 1; i < lengthOfRes; i++) {
    const x = (i * canvas.width) / lengthOfRes
    if (i % (lengthOfRes / 4) === 0) context.fillRect(x - 2, 0, 4, canvas.height)
    context.fillRect(x, 0, 1, canvas.height)
  }
}

const useRerender = (resolution: BeatResolution) => {
  const canvasRef = useRef<HTMLCanvasElement>()
  useLayoutEffect(() => {
    PianoRollStateManager.subscribe(
      'grid',
      (state) => render(canvasRef.current, resolution, state),
      false
    )
    return () => PianoRollStateManager.unsubscribe('grid')
  }, [resolution])
  useEffect(() => {
    render(canvasRef.current, resolution, PianoRollStateManager.state)
  }, [resolution])
  return canvasRef
}

const Grid: FC = () => {
  const loop = useCurrentLoop()
  const { resolution } = loop.melodicLoop.settings
  const ref = useRerender(resolution)
  useSnapManager(resolution)
  return <GridCanvas ref={ref} />
}

export default observer(Grid)
